import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * Lightweight Custom Dropdown Select
 * Clean text label trigger with subtle chevron.
 * Dropdown options display clean primary label with helpful secondary description.
 * No heavy icons or badge clutter.
 */
export default function PolicyCustomSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select option...',
  disabled = false,
  className = '',
  id,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption =
    options.find((opt) => String(opt.id || opt.value) === String(value)) || options[0] || null;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedHeight = Math.min(options.length * 52 + 12, 340);
    const shouldOpenUpwards = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    setMenuStyle({
      position: 'absolute',
      top: shouldOpenUpwards
        ? `${rect.top + window.scrollY - estimatedHeight - 4}px`
        : `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      width: `${Math.max(rect.width, 240)}px`,
      maxHeight: `${Math.min(shouldOpenUpwards ? spaceAbove - 16 : spaceBelow - 16, 340)}px`,
      zIndex: 999999,
    });
  }, [options.length]);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleScrollOrResize = () => {
      updatePosition();
    };

    const handleClickOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, updatePosition]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleSelect = (opt, e) => {
    e.preventDefault();
    e.stopPropagation();
    const val = opt.id !== undefined ? opt.id : opt.value;
    if (onChange) {
      onChange(val);
    }
    setIsOpen(false);
  };

  return (
    <div className={`pcs-container ${className}`} id={id}>
      <button
        ref={triggerRef}
        type="button"
        className={`pcs-trigger ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="pcs-trigger-label">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <i className={`fas fa-chevron-down pcs-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="pcs-menu"
            style={menuStyle}
            role="listbox"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pcs-menu-scroll">
              {options.map((opt) => {
                const optVal = opt.id !== undefined ? opt.id : opt.value;
                const isSelected =
                  selectedOption &&
                  (selectedOption.id !== undefined ? selectedOption.id : selectedOption.value) ===
                    optVal;

                return (
                  <button
                    key={String(optVal)}
                    type="button"
                    className={`pcs-menu-item ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => handleSelect(opt, e)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="pcs-item-text">
                      <div className="pcs-item-title">{opt.label}</div>
                      {opt.description && <div className="pcs-item-desc">{opt.description}</div>}
                    </div>

                    {isSelected && (
                      <div className="pcs-item-check">
                        <i className="fas fa-check" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
